import { inherits as _inherits, createSuper as _createSuper, classCallCheck as _classCallCheck, defineProperty as _defineProperty, assertThisInitialized as _assertThisInitialized, createClass as _createClass, objectWithoutProperties as _objectWithoutProperties, toPropertyKey as _toPropertyKey, objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import _pt from '../../node_modules/prop-types/index.js';
import React from 'react';
import FormContext$1 from './FormContext.js';
import { TriggerEvent, TriggerAction, FormChangeSource } from './constants.js';
import { fieldFormState, isValueSet } from './utils.js';

/**
 * A form listener to trigger actions upon field events
 */

var FieldTrigger = /*#__PURE__*/function (_React$Component) {
  _inherits(FieldTrigger, _React$Component);

  var _super = _createSuper(FieldTrigger);

  function FieldTrigger() {
    var _this;

    _classCallCheck(this, FieldTrigger);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty(_assertThisInitialized(_this), "_prevContext", void 0);

    _defineProperty(_assertThisInitialized(_this), "_matchWarning", false);

    _defineProperty(_assertThisInitialized(_this), "_targetWarning", false);

    _defineProperty(_assertThisInitialized(_this), "_valueWarning", false);

    _defineProperty(_assertThisInitialized(_this), "validateProps", function () {
      var _this$props = _this.props,
          event = _this$props.event,
          match = _this$props.match,
          action = _this$props.action,
          target = _this$props.target,
          value = _this$props.value;

      if (!match && !_this._matchWarning && event === TriggerEvent.OnMatch) {
        console.warn("Trigger match prop is required for ".concat(event, " events"));
        _this._matchWarning = true;
      }

      if ((!target || Array.isArray(target) && target.length === 0) && !_this._targetWarning && (action === TriggerAction.Change || action === TriggerAction.Clear || action === TriggerAction.Set)) {
        console.warn("Trigger target prop is required for ".concat(action, " actions"));
        _this._targetWarning = true;
      }

      if (value === undefined && !_this._valueWarning && (action === TriggerAction.Change || action === TriggerAction.Set)) {
        console.warn("Trigger value prop is required for ".concat(action, " actions"));
        _this._valueWarning = true;
      }
    });

    _defineProperty(_assertThisInitialized(_this), "componentDidUpdate", function () {
      _this.validateProps();

      if (_this.hasValidSource() && _this.hasTriggeredEvent()) {
        _this.handleAction();
      }

      _this._prevContext = _this.context;
    });

    _defineProperty(_assertThisInitialized(_this), "hasValidSource", function () {
      var source = _this.props.source;

      if (source) {
        var changeMetadata = _this.context.previousChange;

        if (typeof source === "function") {
          return !!source(changeMetadata);
        }

        var allowedSources = Array.isArray(source) ? source : [source];

        if (!(changeMetadata !== null && changeMetadata !== void 0 && changeMetadata.source) || !allowedSources.includes(changeMetadata.source)) {
          // change was triggered by an unknown or invalid source
          return false;
        }
      }

      return true;
    });

    _defineProperty(_assertThisInitialized(_this), "hasTriggeredEvent", function () {
      var _this$_prevContext;

      var currentContextValue = _this.context.value || {};
      var prevContextValue = ((_this$_prevContext = _this._prevContext) === null || _this$_prevContext === void 0 ? void 0 : _this$_prevContext.value) || {};
      var _this$props2 = _this.props,
          field = _this$props2.field,
          event = _this$props2.event,
          match = _this$props2.match;
      var fieldArray = Array.isArray(field) ? field : [field];

      for (var i = 0; i < fieldArray.length; i++) {
        var currentValue = currentContextValue[fieldArray[i]];
        var prevValue = prevContextValue[fieldArray[i]];

        if (currentValue !== prevValue) {
          switch (event) {
            // field value has changed
            case TriggerEvent.OnChange:
              return true;
            // field value was set but has now been cleared

            case TriggerEvent.OnClear:
              if (!isValueSet(currentValue)) {
                return true;
              }

              break;
            // field value was not set but is now

            case TriggerEvent.OnSet:
              if (!isValueSet(prevValue)) {
                return true;
              }

              break;
            // field value changed and equals match prop

            case TriggerEvent.OnMatch:
              var hasMatch = typeof match === "function" ? match(fieldFormState(_this.context)) : currentValue === match;
              if (hasMatch) return true;
              break;
          }
        }
      }

      return false;
    });

    _defineProperty(_assertThisInitialized(_this), "handleAction", function () {
      var _this$props3 = _this.props,
          action = _this$props3.action,
          field = _this$props3.field,
          target = _this$props3.target,
          value = _this$props3.value,
          metadata = _this$props3.metadata;

      switch (action) {
        // updating the target field
        case TriggerAction.Change:
        case TriggerAction.Set:
          {
            if (target === undefined) break;
            var targetArray = Array.isArray(target) ? target : [target];
            var newValue = typeof value === "function" ? value(fieldFormState(_this.context)) : value;

            var applyChanges = function applyChanges(prevValue) {
              // for set action, only update targets that are not previously set
              var updateTargets = action === TriggerAction.Set ? targetArray.filter(function (t) {
                return !prevValue || !isValueSet(prevValue[t]);
              }) : targetArray;

              if (updateTargets.length === 0) {
                return prevValue;
              } // skip if no values to apply


              if (prevValue && updateTargets.every(function (t) {
                return prevValue.hasOwnProperty(t) && prevValue[t] === newValue;
              })) {
                return prevValue;
              }

              return updateTargets.reduce(function (acc, t) {
                return _objectSpread2(_objectSpread2({}, acc), {}, _defineProperty({}, t, newValue));
              }, _objectSpread2({}, prevValue));
            };

            _this.context.onChange(target, newValue, applyChanges, metadata !== null && metadata !== void 0 ? metadata : {
              source: FormChangeSource.Trigger,
              triggerField: field
            });

            break;
          }
        // clear the target field

        case TriggerAction.Clear:
          {
            if (target === undefined) break;

            var _targetArray = Array.isArray(target) ? target : [target];

            var _applyChanges = function _applyChanges(prevValue) {
              // only clear targets that have values
              var clearTargets = _targetArray.filter(function (t) {
                return prevValue && prevValue[t] !== undefined;
              });

              if (!prevValue || clearTargets.length === 0) {
                return prevValue;
              }

              return clearTargets.reduce(function (acc, t) {
                acc[t];

                var otherValues = _objectWithoutProperties(acc, [t].map(_toPropertyKey));

                return otherValues;
              }, prevValue);
            };

            _this.context.onChange(target, undefined, _applyChanges, metadata !== null && metadata !== void 0 ? metadata : {
              source: FormChangeSource.Trigger,
              triggerField: field
            });

            break;
          }
        // perform a custom function

        default:
          if (typeof action === "function") {
            var _formState = fieldFormState(_this.context);

            action(_formState);
          }

      }
    });

    _defineProperty(_assertThisInitialized(_this), "shouldComponentUpdate", function () {
      return false;
    });

    _defineProperty(_assertThisInitialized(_this), "render", function () {
      return null;
    });

    return _this;
  }

  _createClass(FieldTrigger, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      this._prevContext = this.context;
      this.validateProps();
    }
  }]);

  return FieldTrigger;
}(React.Component);

_defineProperty(FieldTrigger, "propTypes", {
  field: _pt.oneOfType([_pt.string, _pt.arrayOf(_pt.string)]).isRequired,
  source: _pt.oneOfType([_pt.any, _pt.arrayOf(_pt.any), _pt.func]),
  event: _pt.any,
  match: _pt.any,
  action: _pt.oneOfType([_pt.any, _pt.func]).isRequired,
  target: _pt.oneOfType([_pt.string, _pt.arrayOf(_pt.string)]),
  value: _pt.any,
  metadata: _pt.any
});

_defineProperty(FieldTrigger, "defaultProps", {
  event: TriggerEvent.OnChange
});

_defineProperty(FieldTrigger, "contextType", FormContext$1);

var FieldTrigger$1 = FieldTrigger;

export { FieldTrigger$1 as default };
